import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react'
import useDataLoader from '../hooks/useDataLoader'
import { runDataAudit } from '../services/dataAudit'
import { applyFilters, EMPTY_FILTERS } from '../services/dataFilters'
import { getAuditRows, getLargeDatasetInfo } from '../services/largeDataset'

const DataContext = createContext(null)

export function DataProvider({ children }) {
  const dataLoader = useDataLoader()
  const { data, userData, dataSource } = dataLoader

  const [dataFilters, setDataFilters] = useState(EMPTY_FILTERS)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(10)
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null)

  const filteredData = useMemo(() => applyFilters(data, dataFilters), [data, dataFilters])

  // When filters are active, derive the matching raw rows from survivingIndices
  // so the DataGrid's userDataset stays in sync with the filtered dataset.
  const filteredUserData = useMemo(() => {
    const indices = filteredData?.survivingIndices
    if (!indices || !userData) return userData
    return indices.map(i => userData[i])
  }, [filteredData, userData])

  const [auditSummary, setAuditSummary] = useState(null)
  const auditTimerRef = useRef(null)

  useEffect(() => {
    if (auditTimerRef.current) clearTimeout(auditTimerRef.current)
    if (!userData) {
      setAuditSummary(null)
      return
    }
    auditTimerRef.current = setTimeout(() => {
      const rows = getAuditRows(userData)
      const summary = runDataAudit(rows, {
        totalRows: userData.length,
        sampled: getLargeDatasetInfo(userData).auditSampled,
      })
      setAuditSummary(summary)
    }, 0)
    return () => {
      if (auditTimerRef.current) clearTimeout(auditTimerRef.current)
    }
  }, [userData])

  const largeDatasetInfo = useMemo(() => getLargeDatasetInfo(userData || []), [userData])

  useEffect(() => {
    setDataFilters(EMPTY_FILTERS)
  }, [userData])

  useEffect(() => {
    setAutoRefreshEnabled(false)
    setLastRefreshedAt(null)
  }, [dataSource])

  return (
    <DataContext.Provider
      value={{
        dataLoader,
        ...dataLoader,
        filteredData,
        filteredUserData,
        dataFilters,
        setDataFilters,
        auditSummary,
        largeDatasetInfo,
        autoRefreshEnabled,
        setAutoRefreshEnabled,
        autoRefreshInterval,
        setAutoRefreshInterval,
        lastRefreshedAt,
        setLastRefreshedAt,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}

export function useDataContext() {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useDataContext must be used within DataProvider')
  return ctx
}
